import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Row, Card, Form, Col } from "react-bootstrap";
import {
  FormState,
  FormValue,
  initialFormState,
  invalidateStates,
} from "./FormState";
import { ValidationErrorResponse } from "../../../services/ValidationError";
import SuccessButton from "../../organisms/SuccessButton";
import ServerValidationErrorAlert from "../../organisms/ServerValidationErrorAlert";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import ServerErrorAlert from "../../organisms/ServerErrorAlert";
import LoadingScreen from "../../organisms/LoadingScreen";
import ValidationError from "../../../services/ValidationError";
import { capitalize } from "../../../utils/textUtils";
import {
  leerRazas,
  Sexo,
  Raza,
  Temperamento,
} from "../../../services/mascotasServicios";
import { Typeahead } from "react-bootstrap-typeahead";
import { cargarMascota, editarMascota } from "./actions";
import { StyledFormControl } from "../../organisms/StyledFormControl";
import CardTitle from "../../organisms/CardTitle";
import Exito, { ExitoOption } from "../../organisms/Exito";

function FormSection() {
  const [validatedForm, setValidatedForm] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [serverFailedValidations, setServerFailedValidations] = useState(
    {} as ValidationErrorResponse
  );
  const [serverErrorOcurred, setServerErrorOcurred] = useState(false);
  const [razas, setRazas] = useState([] as Array<Raza>);
  const [isFinished, setIsFinished] = useState(false);

  let { idMascota } = useParams();

  useEffect(() => {
    async function poblarRazas() {
      setServerErrorOcurred(false);
      try {
        const razasResponse = await leerRazas();
        setRazas(razasResponse);
      } catch (error) {
        setServerErrorOcurred(true);
      }
    }

    poblarRazas();
  }, []);

  useEffect(() => {
    async function inicializarMascota(id: string) {
      setServerErrorOcurred(false);
      try {
        const _formState = await cargarMascota(id);
        if (_formState) {
          setFormState(_formState);
        }
      } catch (error) {
        setServerErrorOcurred(true);
      }
    }

    if (idMascota) {
      inicializarMascota(idMascota);
    }
  }, [idMascota]);

  const raza = useMemo(() => {
    if (razas && razas.length > 0) {
      return razas.find((raza) => raza.id === formState.raza.value);
    }
    return {} as Raza;
  }, [formState.raza.value, razas]);

  const save = async () => {
    setServerFailedValidations({} as ValidationErrorResponse);
    setServerErrorOcurred(false);
    setIsLoading(true);
    try {
      if (idMascota) {
        await editarMascota(formState, idMascota);
        setIsFinished(true);
      }
    } catch (error: any) {
      if (error instanceof ValidationError) {
        invalidateStates(formState, error.getFailedValidations());
        setServerFailedValidations(error.getFailedValidations());
      } else {
        setServerErrorOcurred(true);
      }
    }

    setIsLoading(false);
  };

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;

    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      save();
    }

    setValidatedForm(true);
  };

  // setCustomValidity integra el Typeahead con la validación HTML5 del form
  // (required por sí solo no basta: el input tiene texto pero sin selección válida)
  const onRazaBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const razaEsValida = formState.raza.value !== "" && razas.find(r => r.id === formState.raza.value);
    if (!razaEsValida) {
      input.setCustomValidity("Raza debe estar en el listado");
      setFormState({
        ...formState,
        raza: { ...formState.raza, isValid: false, wasTouched: true }
      });
    } else {
      input.setCustomValidity("");
    }
  };

  const onInputChanged = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      value: target.value,
      isValid: target.checkValidity(),
      wasTouched: true,
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onInputChangedCapital = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      value: capitalize(target.value),
      isValid: target.checkValidity(),
      wasTouched: true,
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onEliminarFotoToggle = () => {
    const newInputState = {
      ...formState.eliminarFoto,
      value: !formState.eliminarFoto.value,
      isValid: true,
      wasTouched: true,
    };
    setFormState({ ...formState, eliminarFoto: newInputState });
  };

  const onEliminarCertificadoVacuna = () => {
    const newInputState = {
      ...formState.eliminarCertificadoVacuna,
      value: !formState.eliminarCertificadoVacuna.value,
      isValid: true,
      wasTouched: true,
    };
    setFormState({ ...formState, eliminarCertificadoVacuna: newInputState });
  };

  const onCheckChanged = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      value: !formState[target.name as keyof FormState].value,
      isValid: target.checkValidity(),
      wasTouched: true,
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  const onFileChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      const newInputState = {
        ...formState[event.currentTarget.name as keyof FormState],
        value: selectedFile,
        isValid: event.currentTarget.checkValidity(),
        wasTouched: true,
      };
      setFormState({ ...formState, [event.currentTarget.name]: newInputState });
    }
  };

  const onTypeAheadChanged = (
    selected: any[],
    name: string,
    options: any[]
  ) => {
    if (selected && selected.length > 0) {
      const selectedValueId = selected[0]["id"];
      const validValue = options.find((opt) => opt["id"] === selectedValueId);
      const newInputState = {
        ...formState[name as keyof FormState],
        value: selectedValueId,
        isValid: validValue,
        wasTouched: true,
      };
      setFormState({ ...formState, [name]: newInputState });
    } else {
      const newInputState = {
        ...formState[name as keyof FormState],
        value: "",
        isValid: false,
        wasTouched: true,
      };
      setFormState({ ...formState, [name]: newInputState });
    }
  };

  const handleBlur = ({ target }: any) => {
    const newInputState = {
      ...formState[target.name as keyof FormState],
      wasTouched: true,
    };
    setFormState({ ...formState, [target.name]: newInputState });
  };

  if (isFinished) {
    return <Exito title={"¡Modificación exitosa!"} options={exitoOptions} />;
  }

  return (
    <Row className="justify-content-center">
      <div className="col-xs-12 col-md-10 col-lg-8 col-xxl-5">
        <LoadingScreen isLoading={isLoading} />
        <Card>
          <StyledCardHeader className="pt-4 pb-4">
            <CardTitle>Edita Una Mascota</CardTitle>
          </StyledCardHeader>
          <Card.Body>
            {serverFailedValidations &&
              Object.keys(serverFailedValidations).length > 0 && (
                <Row className="justify-content-center mb-3">
                  <ServerValidationErrorAlert
                    serverFailedValidations={serverFailedValidations}
                    wrapperClass="col col-9"
                  />
                </Row>
              )}

            {serverErrorOcurred && (
              <Row className="justify-content-center mb-3">
                <ServerErrorAlert class="col col-9" />
              </Row>
            )}

            {serverErrorOcurred && <ServerErrorModal />}

            <Row className="justify-content-center">
              <Form
                className="col col-9"
                noValidate
                validated={validatedForm}
                onSubmit={handleSubmit}
              >
                <h5 className="mb-3">Datos Básicos</h5>

                <Form.Group
                  as={Col}
                  controlId="formPropietario"
                  className="mb-2"
                >
                  <Form.Label>Propietario</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="nombre"
                    value={formState.nombresPropietario.value}
                    readOnly
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="formNombre" className="mb-2">
                  <Form.Label>Nombre</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="nombre"
                    value={formState.nombre.value}
                    onChange={onInputChangedCapital}
                    required
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.nombre)}
                    isInvalid={isFormValueInvalid(formState.nombre)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} controlId="formSexo" className="mb-3">
                  <Form.Label>Sexo</Form.Label>
                  <Form.Check
                    type="radio"
                    id="radio_hembra"
                    label="Hembra"
                    value={Sexo.Hembra.toString()}
                    name="sexo"
                    required
                    onChange={onInputChanged}
                    checked={formState.sexo.value === Sexo.Hembra.toString()}
                  />

                  <Form.Check
                    type="radio"
                    id="radio_macho"
                    label="Macho"
                    value={Sexo.Macho.toString()}
                    name="sexo"
                    required
                    onChange={onInputChanged}
                    checked={formState.sexo.value === Sexo.Macho.toString()}
                  />

                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>

                {raza?.id && (
                  <Form.Group as={Col} controlId="formRaza" className="mb-2">
                    <Form.Label>Raza</Form.Label>
                    <Typeahead
                      defaultSelected={[raza]}
                      clearButton
                      id="input-raza"
                      options={razas}
                      labelKey="nombre"
                      inputProps={{ required: true }}
                      onBlur={onRazaBlur}
                      onChange={(selected) =>
                        onTypeAheadChanged(selected, "raza", razas)
                      }
                      isValid={isRequiredFormValueValid(formState.raza)}
                      isInvalid={isRequiredFormValueInvalid(formState.raza)}
                    />
                    {isRequiredFormValueInvalid(formState.raza) && (
                      <StyledInvalidFeedback>
                        Raza debe estar en el listado.
                      </StyledInvalidFeedback>
                    )}
                  </Form.Group>
                )}
                {!raza?.id && (
                  <Form.Group as={Col} controlId="formRaza" className="mb-2">
                    <Form.Label>Raza</Form.Label>
                    <Typeahead
                      clearButton
                      id="input-raza"
                      options={razas}
                      labelKey="nombre"
                      inputProps={{ required: true }}
                      onBlur={onRazaBlur}
                      onChange={(selected) =>
                        onTypeAheadChanged(selected, "raza", razas)
                      }
                      isValid={isRequiredFormValueValid(formState.raza)}
                      isInvalid={isRequiredFormValueInvalid(formState.raza)}
                    />
                    {isRequiredFormValueInvalid(formState.raza) && (
                      <StyledInvalidFeedback>
                        Raza debe estar en el listado.
                      </StyledInvalidFeedback>
                    )}
                  </Form.Group>
                )}

                <Form.Group as={Col} controlId="formFechaNac" className="mb-2">
                  <Form.Label>Fecha de Nacimiento</Form.Label>
                  <StyledFormControl
                    type="date"
                    name="fechaNacimiento"
                    value={formState.fechaNacimiento.value}
                    onChange={onInputChanged}
                    required
                    isValid={isFormValueValid(formState.fechaNacimiento)}
                    isInvalid={isFormValueInvalid(formState.fechaNacimiento)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formFechaIngreso"
                  className="mb-5"
                >
                  <Form.Label>Fecha de Ingreso</Form.Label>
                  <StyledFormControl
                    type="date"
                    name="fechaIngreso"
                    value={formState.fechaIngreso.value}
                    onChange={onInputChanged}
                    required
                    isValid={isFormValueValid(formState.fechaIngreso)}
                    isInvalid={isFormValueInvalid(formState.fechaIngreso)}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>

                <h5 className="mb-3">Anexos</h5>

                <Form.Group
                  as={Col}
                  controlId="formTipoModificacionFoto"
                  className="mb-3"
                >
                  <Form.Label>¿Cómo modificará la foto?</Form.Label>

                  <Form.Check
                    type="radio"
                    id="tipo_modificacion_foto_cargar"
                    label="Cargar nueva foto"
                    name="eliminarFoto"
                    onChange={onEliminarFotoToggle}
                    checked={!formState.eliminarFoto.value}
                  />

                  <Form.Check
                    type="radio"
                    id="tipo_modificacion_foto_eliminar"
                    label="Eliminar foto"
                    name="eliminarFoto"
                    onChange={onEliminarFotoToggle}
                    checked={formState.eliminarFoto.value}
                  />
                </Form.Group>

                {!formState.eliminarFoto.value && (
                  <Form.Group
                    as={Col}
                    controlId="formFoto"
                    xs="auto"
                    className="mb-3"
                  >
                    <Form.Label>Foto</Form.Label>
                    <StyledFormControl
                      type="file"
                      name="foto"
                      onChange={onFileChanged}
                      onBlur={handleBlur}
                      isValid={isFormValueValid(formState.foto)}
                      isInvalid={isFormValueInvalid(formState.foto)}
                    />
                    <Form.Text muted>Opcional.</Form.Text>
                  </Form.Group>
                )}

                <Form.Group
                  as={Col}
                  controlId="formTipoModificacionCertificado"
                  className="mb-3"
                >
                  <Form.Label>¿Cómo modificará el certificado?</Form.Label>

                  <Form.Check
                    type="radio"
                    id="tipo_modificacion_certificado_cargar"
                    label="Cargar nuevo certificado"
                    name="eliminarCertificadoVacuna"
                    onChange={onEliminarCertificadoVacuna}
                    checked={!formState.eliminarCertificadoVacuna.value}
                  />

                  <Form.Check
                    type="radio"
                    id="tipo_modificacion_certificado_eliminar"
                    label="Eliminar Certificado"
                    name="eliminarCertificadoVacuna"
                    onChange={onEliminarCertificadoVacuna}
                    checked={formState.eliminarCertificadoVacuna.value}
                  />
                </Form.Group>

                {!formState.eliminarCertificadoVacuna.value && (
                  <Form.Group
                    as={Col}
                    controlId="formCertificadoVacuna"
                    xs="auto"
                    className="mb-5"
                  >
                    <Form.Label>Certificado Vacuna</Form.Label>
                    <StyledFormControl
                      type="file"
                      name="certificadoVacuna"
                      onChange={onFileChanged}
                      onBlur={handleBlur}
                      isValid={isFormValueValid(formState.certificadoVacuna)}
                      isInvalid={isFormValueInvalid(
                        formState.certificadoVacuna
                      )}
                    />
                    <Form.Text muted>Opcional.</Form.Text>
                  </Form.Group>
                )}

                <h5 className="mb-3">Información médica</h5>

                <Form.Group className="mb-3" controlId="formEstaEsterilizada">
                  <Form.Check
                    id="checkEstaEsterilizada"
                    name="estaEsterilizada"
                    type="checkbox"
                    label="Está esterilizada"
                    onChange={onCheckChanged}
                    value={formState.estaEsterilizada.value.toString()}
                    checked={formState.estaEsterilizada.value}
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formFechaGarrapaticida"
                  className="mb-2"
                >
                  <Form.Label>Fecha de Último Garrapaticida</Form.Label>
                  <StyledFormControl
                    type="date"
                    name="fechaGarrapaticida"
                    value={formState.fechaGarrapaticida.value}
                    onChange={onInputChanged}
                    isValid={isFormValueValid(formState.fechaGarrapaticida)}
                    isInvalid={isFormValueInvalid(formState.fechaGarrapaticida)}
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formMesesPeriodicidadGarrapaticida"
                  className="mb-3"
                >
                  <Form.Label>Periodicidad de Garrapaticida (Meses)</Form.Label>
                  <StyledFormControl
                    type="number"
                    name="mesesPeriodicidadGarrapaticida"
                    value={formState.mesesPeriodicidadGarrapaticida.value}
                    onChange={onInputChanged}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(
                      formState.mesesPeriodicidadGarrapaticida
                    )}
                    isInvalid={isFormValueInvalid(
                      formState.mesesPeriodicidadGarrapaticida
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    Campo debe ser un entero.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formFechaVacuna"
                  className="mb-3"
                >
                  <Form.Label>Fecha de última Vacuna</Form.Label>
                  <StyledFormControl
                    type="date"
                    name="fechaVacuna"
                    value={formState.fechaVacuna.value}
                    onChange={onInputChanged}
                    isValid={isFormValueValid(formState.fechaVacuna)}
                    isInvalid={isFormValueInvalid(formState.fechaVacuna)}
                  />
                </Form.Group>

                <Form.Group
                  className="mb-3"
                  controlId="formTieneCondicionMedica"
                >
                  <Form.Check
                    id="checkTieneCondicionMedica"
                    name="tieneCondicionMedica"
                    type="checkbox"
                    label="Tiene condición médica"
                    onChange={onCheckChanged}
                    value={formState.tieneCondicionMedica.value.toString()}
                    checked={formState.tieneCondicionMedica.value}
                  />
                  <Form.Text muted>Opcional.</Form.Text>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formObservacionesMedicas"
                  className="mb-2"
                >
                  <Form.Label>Observaciones Médicas</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="observacionesMedicas"
                    value={formState.observacionesMedicas.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    rows={3}
                  />
                  <Form.Text muted>Opcional.</Form.Text>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formNombreVeterinario"
                  className="mb-5"
                >
                  <Form.Label>Nombre del Veterinario</Form.Label>
                  <StyledFormControl
                    type="text"
                    name="nombreVeterinario"
                    value={formState.nombreVeterinario.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    isValid={isFormValueValid(formState.nombreVeterinario)}
                    isInvalid={isFormValueInvalid(formState.nombreVeterinario)}
                  />
                  <Form.Text muted>Opcional.</Form.Text>
                </Form.Group>

                <h5 className="mb-3">Información de comportamiento</h5>

                <Form.Group
                  as={Col}
                  controlId="formTemperamento"
                  className="mb-3"
                >
                  <Form.Label>Temperamento</Form.Label>
                  <Form.Check
                    type="radio"
                    id="radio_agresivo"
                    label="Agresivo"
                    value={Temperamento.Agresivo.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Agresivo.toString()
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="radio_temperamental"
                    label="Temperamental"
                    value={Temperamento.Temperamental.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Temperamental.toString()
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="radio_balanceado"
                    label="Balanceado"
                    value={Temperamento.Balanceado.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Balanceado.toString()
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="radio_sumiso"
                    label="Sumiso"
                    value={Temperamento.Sumiso.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Sumiso.toString()
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="radio_dominante"
                    label="Dominante"
                    value={Temperamento.Dominante.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Dominante.toString()
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="radio_amigable"
                    label="Amigable"
                    value={Temperamento.Amigable.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Amigable.toString()
                    }
                  />

                  <Form.Check
                    type="radio"
                    id="radio_temeroso"
                    label="Temeroso"
                    value={Temperamento.Temeroso.toString()}
                    name="temperamento"
                    required
                    onChange={onInputChanged}
                    checked={
                      formState.temperamento.value ===
                      Temperamento.Temeroso.toString()
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    Campo debe ser diligenciado.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formObservacionesTemperamento"
                  className="mb-2"
                >
                  <Form.Label>Observaciones de Temperamento</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="observacionesTemperamento"
                    value={formState.observacionesTemperamento.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    rows={3}
                  />
                  <Form.Text muted>Opcional.</Form.Text>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formObservacionesAlimenticias"
                  className="mb-2"
                >
                  <Form.Label>Observaciones Alimenticias</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="observacionesAlimenticias"
                    value={formState.observacionesAlimenticias.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    rows={3}
                  />
                  <Form.Text muted>Opcional.</Form.Text>
                </Form.Group>

                <Form.Group
                  as={Col}
                  controlId="formComentarios"
                  className="mb-2"
                >
                  <Form.Label>Comentarios Generales</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="comentarios"
                    value={formState.comentarios.value}
                    onChange={onInputChangedCapital}
                    onBlur={handleBlur}
                    rows={3}
                  />
                  <Form.Text muted>Opcional.</Form.Text>
                </Form.Group>

                <ButtonsCol className="d-flex justify-content-center">
                  <SuccessButton type="submit" variant="success">
                    Guardar
                  </SuccessButton>
                </ButtonsCol>
              </Form>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </Row>
  );
}

const ButtonsCol = styled(Col)`
  gap: 1%;
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`;

const StyledInvalidFeedback = styled.div`
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
  display: block;
`;

const isFormValueValid = (formValue: FormValue): boolean =>
  formValue.isValid === true && formValue.wasTouched === true;
const isRequiredFormValueValid = (formValue: FormValue): boolean =>
  formValue.isValid === true;
const isRequiredFormValueInvalid = (formValue: FormValue): boolean =>
  formValue.isValid === false;
const isFormValueInvalid = (formValue: FormValue): boolean =>
  formValue.isValid === false && formValue.wasTouched === true;

const exitoOptions: ExitoOption[] = [
  {
    description: "Ver listado",
    path: "/mascotas",
    variant: "primary",
    isLast: true,
  },
];

export default FormSection;
